import { useCallback, useEffect, useRef, useState } from "react";

import {
  getUserGroupAlbums,
  getUserGroupAlbum,
} from "../services/userGroupAlbumsService";

const ALBUM_LIMIT = 12;
const IMAGE_LIMIT = 50;

const mergeById = (previous, incoming, field) => {
  const map = new Map();

  [...previous, ...incoming].forEach((item) => {
    const key = item?.[field];

    if (key != null) {
      map.set(String(key), item);
    }
  });

  return [...map.values()];
};

const getErrorMessage = (error, fallback) =>
  error?.response?.data?.message || error?.message || fallback;

export const useGroupAlbums = ({ groupTag } = {}) => {
  const [albums, setAlbums] = useState([]);
  const [album, setAlbum] = useState(null);
  const [images, setImages] = useState([]);

  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadingAlbum, setLoadingAlbum] = useState(false);
  const [loadingMoreImages, setLoadingMoreImages] = useState(false);

  const [error, setError] = useState("");
  const [albumError, setAlbumError] = useState("");

  const [hasMore, setHasMore] = useState(false);
  const [hasMoreImages, setHasMoreImages] = useState(false);

  const albumOffsetRef = useRef(0);
  const imageOffsetRef = useRef(0);

  const albumGeneration = useRef(0);
  const detailGeneration = useRef(0);

  const albumRequest = useRef(false);
  const imageRequest = useRef(false);

  const selectedAlbumTagRef = useRef(null);

  const loadAlbums = useCallback(
    async ({ reset = false } = {}) => {
      if (!groupTag) return;

      if (albumRequest.current && !reset) {
        return;
      }

      const generation = reset
        ? ++albumGeneration.current
        : albumGeneration.current;

      albumRequest.current = true;

      const offset = reset ? 0 : albumOffsetRef.current;

      if (reset) {
        albumOffsetRef.current = 0;
        setAlbums([]);
        setHasMore(false);
        setLoading(true);
        setLoadingMore(false);
      } else {
        setLoadingMore(true);
      }

      setError("");

      try {
        const response = await getUserGroupAlbums(
          groupTag,
          ALBUM_LIMIT,
          offset,
        );

        if (generation !== albumGeneration.current) {
          return;
        }

        const records = response?.data;

        if (!Array.isArray(records)) {
          throw new Error("Invalid group albums response.");
        }

        setAlbums((previous) =>
          reset ? records : mergeById(previous, records, "albumId"),
        );

        albumOffsetRef.current = offset + records.length;

        setHasMore(records.length === ALBUM_LIMIT);
      } catch (requestError) {
        if (generation === albumGeneration.current) {
          setError(
            getErrorMessage(requestError, "Unable to load group albums."),
          );
        }
      } finally {
        if (generation === albumGeneration.current) {
          albumRequest.current = false;
          setLoading(false);
          setLoadingMore(false);
        }
      }
    },
    [groupTag],
  );

  const closeAlbum = useCallback(() => {
    detailGeneration.current += 1;
    imageRequest.current = false;
    imageOffsetRef.current = 0;
    selectedAlbumTagRef.current = null;

    setAlbum(null);
    setImages([]);
    setHasMoreImages(false);
    setLoadingAlbum(false);
    setLoadingMoreImages(false);
    setAlbumError("");
  }, []);

  const openAlbum = useCallback(
    async (albumTag) => {
      if (!groupTag || !albumTag) return;

      const generation = ++detailGeneration.current;

      selectedAlbumTagRef.current = albumTag;
      imageOffsetRef.current = 0;
      imageRequest.current = true;

      setAlbum(null);
      setImages([]);
      setHasMoreImages(false);
      setLoadingAlbum(true);
      setLoadingMoreImages(false);
      setAlbumError("");

      try {
        const response = await getUserGroupAlbum(
          groupTag,
          albumTag,
          IMAGE_LIMIT,
          0,
        );

        if (generation !== detailGeneration.current) {
          return;
        }

        const record = response?.data;

        if (!record || !Array.isArray(record.images)) {
          throw new Error("Invalid album response.");
        }

        setAlbum(record);
        setImages(record.images);

        imageOffsetRef.current = record.images.length;

        setHasMoreImages(
          record.pagination?.hasMore ?? record.images.length === IMAGE_LIMIT,
        );
      } catch (requestError) {
        if (generation === detailGeneration.current) {
          setAlbumError(
            getErrorMessage(requestError, "Unable to open this album."),
          );
        }
      } finally {
        if (generation === detailGeneration.current) {
          imageRequest.current = false;
          setLoadingAlbum(false);
        }
      }
    },
    [groupTag],
  );

  const loadMoreImages = useCallback(async () => {
    const albumTag = selectedAlbumTagRef.current;

    if (!groupTag || !albumTag || !hasMoreImages || imageRequest.current) {
      return;
    }

    const generation = detailGeneration.current;
    const offset = imageOffsetRef.current;

    imageRequest.current = true;
    setLoadingMoreImages(true);
    setAlbumError("");

    try {
      const response = await getUserGroupAlbum(
        groupTag,
        albumTag,
        IMAGE_LIMIT,
        offset,
      );

      if (generation !== detailGeneration.current) {
        return;
      }

      const record = response?.data;

      if (!record || !Array.isArray(record.images)) {
        throw new Error("Invalid album images response.");
      }

      setImages((previous) => mergeById(previous, record.images, "imageId"));

      imageOffsetRef.current = offset + record.images.length;

      setHasMoreImages(
        record.pagination?.hasMore ?? record.images.length === IMAGE_LIMIT,
      );
    } catch (requestError) {
      if (generation === detailGeneration.current) {
        setAlbumError(
          getErrorMessage(requestError, "Unable to load more images."),
        );
      }
    } finally {
      if (generation === detailGeneration.current) {
        imageRequest.current = false;
        setLoadingMoreImages(false);
      }
    }
  }, [groupTag, hasMoreImages]);

  useEffect(() => {
    albumGeneration.current += 1;
    detailGeneration.current += 1;

    albumRequest.current = false;
    imageRequest.current = false;

    albumOffsetRef.current = 0;
    imageOffsetRef.current = 0;
    selectedAlbumTagRef.current = null;

    setAlbums([]);
    setAlbum(null);
    setImages([]);

    setHasMore(false);
    setHasMoreImages(false);

    setError("");
    setAlbumError("");

    setLoadingMore(false);
    setLoadingAlbum(false);
    setLoadingMoreImages(false);

    if (!groupTag) {
      setLoading(false);
      setError("Group tag is required.");
      return;
    }

    let active = true;
    const generation = albumGeneration.current;

    setLoading(true);
    albumRequest.current = true;

    const fetchInitial = async () => {
      try {
        const response = await getUserGroupAlbums(groupTag, ALBUM_LIMIT, 0);

        if (!active || generation !== albumGeneration.current) {
          return;
        }

        const records = response?.data;

        if (!Array.isArray(records)) {
          throw new Error("Invalid group albums response.");
        }

        setAlbums(records);

        albumOffsetRef.current = records.length;

        setHasMore(records.length === ALBUM_LIMIT);
      } catch (requestError) {
        if (active && generation === albumGeneration.current) {
          setError(
            getErrorMessage(requestError, "Unable to load group albums."),
          );
        }
      } finally {
        if (active && generation === albumGeneration.current) {
          albumRequest.current = false;
          setLoading(false);
        }
      }
    };

    void fetchInitial();

    return () => {
      active = false;
      albumGeneration.current += 1;
      detailGeneration.current += 1;

      albumRequest.current = false;
      imageRequest.current = false;
    };
  }, [groupTag]);

  return {
    albums,
    album,
    images,

    loading,
    loadingMore,
    loadingAlbum,
    loadingMoreImages,

    error,
    albumError,

    hasMore,
    hasMoreImages,

    loadMoreAlbums: () => loadAlbums(),
    refreshAlbums: () => loadAlbums({ reset: true }),
    openAlbum,
    closeAlbum,
    loadMoreImages,
  };
};
